import { useState, useEffect } from "react";
import { API } from "../App";
import axios from "axios";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Calendar, ChevronLeft, ChevronRight, Trophy, ArrowRightLeft, Star, Clock } from "lucide-react";
import SocialShareButtons from "../components/SocialShareButtons";

const categoryIcons = {
  match: Trophy,
  transfer: ArrowRightLeft,
  milestone: Star,
};

const categoryColors = {
  match: "bg-green-100 text-green-700 border-green-200",
  transfer: "bg-blue-100 text-blue-700 border-blue-200",
  milestone: "bg-amber-100 text-amber-700 border-amber-200",
};

export default function OnThisDayPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());

  const month = currentDate.getMonth() + 1;
  const day = currentDate.getDate();

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/on-this-day?month=${month}&day=${day}`)
      .then(res => setEvents(res.data))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [month, day]);

  const goBack = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() - 1);
    setCurrentDate(d);
  };

  const goForward = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + 1);
    setCurrentDate(d);
  };

  const goToday = () => setCurrentDate(new Date());

  const isToday = currentDate.toDateString() === new Date().toDateString();

  const dateStr = currentDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

  // JSON-LD structured data for Google indexing
  const jsonLdData = events.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": `On This Day in West Ham History - ${dateStr}`,
    "description": `Historic West Ham United events on ${dateStr}. ${events.length} event${events.length !== 1 ? 's' : ''} from the club's history since 1895.`,
    "url": `https://allwhufc.com/on-this-day`,
    "numberOfItems": events.length,
    "itemListElement": events.map((event, idx) => ({
      "@type": "ListItem",
      "position": idx + 1,
      "item": {
        "@type": "Article",
        "headline": `${event.year}: ${event.title}`,
        "description": event.description,
        "datePublished": `${event.year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
        "publisher": {
          "@type": "Organization",
          "name": "All WHUFC"
        }
      }
    }))
  } : null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="on-this-day-page">
      <SEO
        title="On This Day"
        path="/on-this-day"
        description={`West Ham United on this day in history (${dateStr}). Discover what happened on every day of the year in the club's rich history from 1895 to the present.`}
        jsonLd={jsonLdData}
        noindex={!loading && events.length === 0 ? "noindex,follow" : false}
      />
      <Navbar />

      <div className="bg-[#7C2C3B] py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            ON THIS DAY
          </h1>
          <p className="text-white/80">Historic West Ham moments from this date in history</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Date Navigator */}
        <div className="flex items-center justify-center gap-4 mb-8" data-testid="date-navigator">
          <button
            onClick={goBack}
            className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
            data-testid="date-prev"
          >
            <ChevronLeft className="w-5 h-5 text-slate-600" />
          </button>
          
          <div className="text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
              {dateStr.toUpperCase()}
            </h2>
            {!isToday && (
              <button onClick={goToday} className="text-xs text-[#2BAEE5] hover:underline mt-1" data-testid="date-today">
                Back to today
              </button>
            )}
          </div>

          <button
            onClick={goForward}
            className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
            data-testid="date-next"
          >
            <ChevronRight className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {/* Events */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-16">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-bold text-slate-400 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              NO EVENTS RECORDED
            </h3>
            <p className="text-slate-400 text-sm">
              No historic West Ham events have been recorded for this date. Try another day!
            </p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-[#7C2C3B]/20 hidden md:block" />

            <div className="space-y-6">
              {events.map((event) => {
                const Icon = categoryIcons[event.category || event.type] || Clock;
                const colorClass = categoryColors[event.category || event.type] || "bg-slate-100 text-slate-700 border-slate-200";
                
                return (
                  <div key={event.id} className="flex gap-4 md:gap-6" data-testid={`otd-event-${event.id}`}>
                    {/* Year marker */}
                    <div className="flex-shrink-0 w-12 relative hidden md:flex flex-col items-center">
                      <div className="w-10 h-10 rounded-full bg-[#7C2C3B] flex items-center justify-center z-10">
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                    </div>

                    {/* Event card */}
                    <div className="flex-1 bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl font-bold text-[#7C2C3B]" style={{ fontFamily: 'Barlow Condensed' }}>
                          {event.year}
                        </span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border capitalize ${colorClass}`}>
                          {event.category || event.type}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                        {event.title}
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        {event.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-8 text-center text-sm text-slate-400">
          {events.length} event{events.length !== 1 ? 's' : ''} recorded for this date
        </div>

        {/* Social Sharing */}
        {events.length > 0 && (
          <SocialShareButtons
            shareText={`On This Day in West Ham history (${dateStr}): ${events[0]?.title || ''}`}
          />
        )}
      </div>

      <Footer />
    </div>
  );
}
